import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { resetPasswordApi } from "../../api/authApi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { RootState } from "../../store/store";
import SimpleCaptcha, { createCaptchaChallenge, type CaptchaChallenge } from "../../components/SimpleCaptcha/SimpleCaptcha";
import { getCaptchaChallengeApi } from "../../api/captchaApi";
import "./ResetPassword.css";

const EyeIcon = FiEye as unknown as React.ComponentType<{ "aria-hidden"?: boolean; focusable?: string }>;
const EyeOffIcon = FiEyeOff as unknown as React.ComponentType<{ "aria-hidden"?: boolean; focusable?: string }>;

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.auth.user);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => String(params.get("token") || "").trim(), [params]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [captchaChallenge, setCaptchaChallenge] = useState<CaptchaChallenge>(createCaptchaChallenge);
  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaError, setCaptchaError] = useState("");

  const refreshCaptcha = useCallback(async () => {
    setCaptchaInput("");
    setCaptchaError("");
    try {
      const challenge = await getCaptchaChallengeApi();
      setCaptchaChallenge(challenge);
    } catch {
      setCaptchaChallenge(createCaptchaChallenge());
    }
  }, []);

  useEffect(() => {
    void refreshCaptcha();
  }, [refreshCaptcha]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token) {
      setError(t("resetPassword.invalidToken"));
      return;
    }
    if (!password.trim() || password.trim().length < 8) {
      setError(t("resetPassword.passwordRule"));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("resetPassword.passwordMismatch"));
      return;
    }
    if (!captchaInput.trim()) {
      setCaptchaError(t("captcha.errorRequired"));
      return;
    }
    if (!captchaChallenge.proof) {
      setCaptchaError(t("system.serverError"));
      return;
    }
    if (captchaInput.trim().toUpperCase() !== captchaChallenge.code.toUpperCase()) {
      setCaptchaError(t("captcha.errorInvalid"));
      void refreshCaptcha();
      return;
    }
    setError("");
    setCaptchaError("");
    setSubmitting(true);
    try {
      await resetPasswordApi({
        token,
        password,
        confirmPassword,
        captcha: {
          input: captchaInput.trim(),
          code: captchaChallenge.code,
          proof: String(captchaChallenge.proof || ""),
        },
      });
      toast.success(t("resetPassword.success"));
      navigate("/");
    } catch (requestError: any) {
      const isCaptchaInvalid = String(requestError?.response?.data?.code || "").trim() === "CAPTCHA_INVALID";
      if (isCaptchaInvalid) {
        setCaptchaError(t("captcha.errorInvalid"));
        void refreshCaptcha();
      } else {
        toast.error(t("resetPassword.error"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (user) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="reset-password-page">
      <form className="reset-password-form" onSubmit={handleSubmit}>
        <h2>{t("resetPassword.title")}</h2>
        <p>{t("resetPassword.subtitle")}</p>
        <label htmlFor="reset-password">{t("resetPassword.password")}</label>
        <div className="reset-password-input-wrap">
          <input
            id="reset-password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              setError("");
            }}
            autoComplete="new-password"
            required
          />
          <button
            type="button"
            className="reset-password-toggle"
            onClick={() => setShowPassword((value) => !value)}
            aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
            aria-pressed={showPassword}
          >
            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        </div>
        <label htmlFor="reset-password-confirm">{t("resetPassword.confirmPassword")}</label>
        <div className="reset-password-input-wrap">
          <input
            id="reset-password-confirm"
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(event) => {
              setConfirmPassword(event.target.value);
              setError("");
            }}
            autoComplete="new-password"
            required
          />
          <button
            type="button"
            className="reset-password-toggle"
            onClick={() => setShowConfirmPassword((value) => !value)}
            aria-label={showConfirmPassword ? "Masquer la confirmation du mot de passe" : "Afficher la confirmation du mot de passe"}
            aria-pressed={showConfirmPassword}
          >
            {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        </div>
        {error ? <p className="reset-password-error">{error}</p> : null}
        <SimpleCaptcha
          idPrefix="reset-password"
          challenge={captchaChallenge}
          value={captchaInput}
          onChange={(value) => {
            setCaptchaInput(value);
            setCaptchaError("");
          }}
          onRefresh={() => {
            void refreshCaptcha();
          }}
          label={t("captcha.label")}
          placeholder={t("captcha.placeholder")}
          refreshLabel={t("captcha.refresh")}
          error={captchaError}
        />
        <button type="submit" disabled={submitting}>
          {submitting ? t("resetPassword.saving") : t("resetPassword.save")}
        </button>
        <Link to="/" className="reset-password-back">
          {t("resetPassword.backHome")}
        </Link>
      </form>
    </div>
  );
};

export default ResetPassword;
